import React, { useState, useEffect, useRef } from "react";
import Button from "components/Button/index";
import WideModal from "components/Models/WideModal";
import "./shareProject.scss";
import { Avatar } from "components/Avatar";
import { Share, ActionEntry } from "@induz/sdk";
// import { ManageUser as MU } from "@induz/sdk";

const detectImageMimeType = (bytes) => {
  if (!bytes || bytes.length < 4) return "image/png";
  const s = Array.from(bytes.slice(0, 12));
  if (s[0] === 0x89 && s[1] === 0x50 && s[2] === 0x4e && s[3] === 0x47) return "image/png";
  if (s[0] === 0xff && s[1] === 0xd8 && s[2] === 0xff) return "image/jpeg";
  if (s[0] === 0x47 && s[1] === 0x49 && s[2] === 0x46) return "image/gif";
  if (s[0] === 0x42 && s[1] === 0x4d) return "image/bmp";
  if (s[0] === 0x52 && s[1] === 0x49 && s[2] === 0x46 && s[3] === 0x46 && s[8] === 0x57 && s[9] === 0x45 && s[10] === 0x42 && s[11] === 0x50) return "image/webp";
  const asciiHead = String.fromCharCode(...bytes.slice(0, 200)).toLowerCase();
  if (asciiHead.includes("<svg")) return "image/svg+xml";
  return "image/png";
};

const normalizeBytes = (bytes) => {
  if (!bytes) return null;
  if (bytes instanceof Uint8Array) return bytes;
  if (bytes instanceof ArrayBuffer) return new Uint8Array(bytes);
  if (Array.isArray(bytes)) return Uint8Array.from(bytes);
  return null;
};

const uint8ToBase64 = (u8) => {
  // Avoid call-stack / argument-limit issues with large arrays
  const chunkSize = 0x8000;
  let binary = "";
  for (let i = 0; i < u8.length; i += chunkSize) {
    const chunk = u8.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, chunk);
  }
  return btoa(binary);
};

const bytesToDataUrl = (bytes) => {
  const u8 = normalizeBytes(bytes);
  if (!u8 || u8.length === 0) return null;
  const base64 = uint8ToBase64(u8);
  const mime = detectImageMimeType(u8);
  return `data:${mime};base64,${base64}`;
};

const normalizeEmail = (email) => (email || "").toString().trim().toLowerCase();

const defaultEntry = () => ({
  id: Date.now().toString() + Math.random().toString(36).substring(2, 7),
  subject: "",
  subjectType: "USER",
  action: "ALLOW",
  opIds: ["Read"], // Default to read-only access
  menuOpen: false, // Add this line
  isNew: true,
});

// Operations available
const OPS = [
  { id: 1, name: "Read" },
  { id: 2, name: "Write" },
  { id: 3, name: "Copy" },
];

// Thumbnail size for profile pictures in Share UI only (smaller than default avatar)
const SHARE_AVATAR_SIZE = 24;

const ShareProject = ({ title, handleCancel, selectedFolder, currentPath, selectedRow }) => {
  const [entries, setEntries] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [teamOptions, setTeamOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [showOptions, setShowOptions] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [availableOps, setAvailableOps] = useState([]);
  const autocompleteRef = useRef(null);
  const [inputMode, setInputMode] = useState("user"); // 'user' or 'team'
  const [inputAction, setInputAction] = useState("ALLOW"); // 'ALLOW' or 'DENY'
  const [hadInitialEntries, setHadInitialEntries] = useState(false);

  // Set up available operations
  useEffect(() => {
    setAvailableOps(
      OPS.map((op) => ({
        value: op.name,
        label: op.name,
      }))
    );
  }, []);

  // Console check: Share UI thumbnail is in use
  useEffect(() => {
    console.log("Share UI thumbnail:", SHARE_AVATAR_SIZE, "px");
  }, []);

  // Fixed: Improved click outside detection with better exclusions for selects
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isSelectClick = event.target.closest(".operations-selector")
        || event.target.closest(".multi-select-menu")
        || event.target.closest(".remove-tag");

      if (isSelectClick) {
        return; // Ignore custom multi-select clicks
      }

      // Close all operation dropdowns when clicking outside
      if (!event.target.closest(".custom-multi-select")) {
        setEntries(prev => {
          return prev.map(entry => ({
            ...entry,
            menuOpen: false
          }));
        });
      }

      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    };


    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch user list
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Use selectedFolder if available, otherwise use selectedRow
        const folderData = selectedFolder || selectedRow;
        if (!folderData || !folderData.name) {
          return;
        }
        const fullPath = currentPath.length > 0
          ? "/" + currentPath.join("/") + "/" + folderData.name
          : "/" + folderData.name;
        const share = new Share(fullPath, folderData.owner);
        const users = await share.listUsers();
        const options = (users || [])
          .map((u) => {
            // Support both legacy shapes and SDK's { userMetas: UserMeta, ... } shape
            const meta = u?.userMetas ?? u?.userMeta ?? u?.meta ?? null;
            const email =
              (meta?.getEmail?.() ?? meta?.email ?? u?.email ?? u?.value ?? u?.label ?? "").toString();
            const emailLower = normalizeEmail(email);
            const pictureBytes =
              meta?.getProfilePicture?.() ??
              meta?.profilePicture ??
              u?.profilePicture ??
              u?.profilePic ??
              null;
            const image = bytesToDataUrl(pictureBytes);
            return email
              ? {
                value: email,
                valueLower: emailLower,
                label: email,
                initial: email.charAt(0).toUpperCase(),
                image,
              }
              : null;
          })
          .filter(Boolean);
        setUserOptions(options);
      } catch (e) {
        console.error("Failed to fetch users:", e);
      }
    };
    fetchUsers();
  }, [currentPath, selectedFolder, selectedRow]);


  useEffect(() => {
    // Modify your fetchTeamsOfUser function to format teams properly
    const fetchTeamsOfUser = async () => {
      try {
        // Use selectedFolder if available, otherwise use selectedRow
        const folderData = selectedFolder || selectedRow;
        if (!folderData || !folderData.name) {
          return;
        }
        const fullPath = currentPath.length > 0
          ? "/" + currentPath.join("/") + "/" + folderData.name
          : "/" + folderData.name;
        const share = new Share(fullPath, folderData.owner);
        const teams = await share.listTeamOfUsers();
        // console.log("Fetched teams:", teams);

        // Format teams similar to users
        const formattedTeams = teams.map(team => ({
          value: team.name || team, // Adjust based on actual team object structure
          label: team.name || team,
          initial: (team.name || team).charAt(0).toUpperCase()
        }));

        setTeamOptions(formattedTeams);
      } catch (e) {
        console.error("Failed to fetch teams:", e);
      }
    };
    fetchTeamsOfUser();
  }, [currentPath, selectedFolder, selectedRow]);


  useEffect(() => {
    const listShares = async () => {
      setIsLoading(true);
      try {
        // Use selectedFolder if available, otherwise use selectedRow
        const folderData = selectedFolder || selectedRow;
        if (!folderData || !folderData.name) {
          setIsLoading(false);
          return;
        }
        const fullPath = currentPath.length > 0
          ? "/" + currentPath.join("/") + "/" + folderData.name
          : "/" + folderData.name;
        const share = new Share(fullPath, folderData.owner);
        const entries = await share.listShares();
        // console.log("Fetched shares:", entries);

        // Set flag if there were any existing entries
        setHadInitialEntries(entries.length > 0);

        // const formattedEntries = entries.map((entry) => ({
        //   subject: entry.getSubject(),
        //   subjectType: entry.getSubjectType(),
        //   action: entry.getAction(),
        //   opIds: entry.getOps(),
        //   menuOpen: false,
        //   isNew: false,
        //   isEditing: false,
        // }));

        const formattedEntries = entries.map((entry) => ({
          id: entry.getSubject() + "-" + entry.getAction(), // or any stable identifier
          subject: entry.getSubject(),
          subjectType: entry.getSubjectType(),
          action: entry.getAction(),
          opIds: entry.getOps(),
          menuOpen: false,
          isNew: false,
          isEditing: true, // Always editable for existing entries
        }));

        // console.log("Formatted entries:", formattedEntries);
        setEntries(formattedEntries);
      } catch (e) {
        const isNotFound = e && e.message && (
          e.message.includes("(7)") || 
          e.message.toLowerCase().includes("not found") || 
          e.message.toLowerCase().includes("object_not_found")
        );
        if (isNotFound) {
          setEntries([]);
        } else {
          console.error("Failed to list shares:", e);
        }
      } finally {
        setIsLoading(false);
      }
    };
    listShares();
  }, [currentPath, selectedFolder, selectedRow]);

  // Filter options as user types
  useEffect(() => {
    if (inputValue) {
      if (inputMode === "user") {
        const filtered = userOptions.filter(option =>
          option.label.toLowerCase().includes(inputValue.toLowerCase()) &&
          !entries.some(entry => entry.subject === option.value)
        );
        setFilteredOptions(filtered);
      } else if (inputMode === "team") {
        // Filter teams similar to users
        const filtered = teamOptions.filter(option =>
          option.label.toLowerCase().includes(inputValue.toLowerCase()) &&
          !entries.some(entry => entry.subject === option.value && entry.subjectType === "TEAM")
        );
        setFilteredOptions(filtered);
      }
    } else {
      setFilteredOptions([]);
    }
  }, [inputValue, userOptions, teamOptions, entries, inputMode]);

  // Remove the duplicate useEffect that filters options


  const updateEntry = (index, key, value) => {
    setEntries((prevEntries) => {
      const newEntries = [...prevEntries];
      if (newEntries[index]) {
        newEntries[index] = { ...newEntries[index], [key]: value };

        // If changing to TEAM, clear the subject if it's an email
        // if (key === "subjectType" && value === "TEAM") {
        //   if (newEntries[index].subject.includes("@")) {
        //     newEntries[index].subject = "";
        //   }
        // }
      }
      return newEntries;
    });
  };

  const removeEntry = (index) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
  };

  // Fix 1: Update the addNewSharedUser function to properly handle DENY actions
  const addNewSharedUser = (option) => {
    const newEntry = defaultEntry();
    newEntry.subject = option.value;
    newEntry.action = inputAction; // Set the action from the toggle
    newEntry.isNew = false;
    setEntries((prev) => [...prev, newEntry]);
    setInputValue("");
    setShowOptions(false);

    // Add a small delay to ensure the DOM has updated before trying to scroll
    setTimeout(() => {
      // The issue was that the denied-access section might not be in DOM yet
      // So we need to check if it exists, and if not, wait again
      const containerSelector = inputAction === "DENY"
        ? ".denied-access .share-entries"
        : ".people-with-access:not(.denied-access) .share-entries";

      const entriesContainer = document.querySelector(containerSelector);

      if (entriesContainer) {
        entriesContainer.scrollTop = entriesContainer.scrollHeight;
      } else if (inputAction === "DENY") {
        // console.log("going to the down of deny container");
        // If deny container doesn't exist yet, try again after slight delay
        setTimeout(() => {
          const denyContainer = document.querySelector(".denied-access .share-entries");
          // console.log("denyContainer", denyContainer);
          if (denyContainer) {
            // console.log("scrolling deny container to bottom");
            denyContainer.scrollTop = denyContainer.scrollHeight;
          }
        }, 100);
      }
    }, 100);
  };

  // // Fix 1: Similar update to addNewEntry function
  // const addNewEntry = () => {
  //   const newEntry = defaultEntry();
  //   newEntry.action = inputAction; // Set the action from the toggle
  //   setEntries(prev => [...prev, newEntry]);
  //   setInputValue("");

  //   // Similar fix as above
  //   setTimeout(() => {
  //     const containerSelector = inputAction === "DENY"
  //       ? ".denied-access .share-entries"
  //       : ".people-with-access:not(.denied-access) .share-entries";

  //     const entriesContainer = document.querySelector(containerSelector);

  //     if (entriesContainer) {
  //       entriesContainer.scrollTop = entriesContainer.scrollHeight;

  //       const inputs = document.querySelectorAll(`${containerSelector} .share-entry-row:last-child .subject-input`);
  //       if (inputs.length) {
  //         inputs[0].focus();
  //       }
  //     } else if (inputAction === "DENY") {
  //       // If deny container doesn't exist yet, try again after rendering
  //       setTimeout(() => {
  //         const denyContainer = document.querySelector(".denied-access .share-entries");
  //         if (denyContainer) {
  //           denyContainer.scrollTop = denyContainer.scrollHeight;

  //           const denyInputs = document.querySelectorAll(".denied-access .share-entry-row:last-child .subject-input");
  //           if (denyInputs.length) {
  //             denyInputs[0].focus();
  //           }
  //         }
  // Disable selecting "Team" in subject type for now
  // (just disable the TEAM option in the select)
  // In the renderEntryRow function, the select for subjectType already exists:
  // <select ...>
  //   <option value="USER">User</option>
  //   <option value="TEAM" disabled>Team</option>
  // </select>
  // So, update the renderEntryRow function's subjectType select as below:

  // In renderEntryRow, replace:
  // <option value="TEAM">Team</option>
  // with:
  // <option value="TEAM" disabled>Team</option>
  //       }, 100);
  //     }
  //   }, 100);
  // };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setShowOptions(true);
  };




  const handleManualEntry = (idx, value) => {
    updateEntry(idx, "subject", value);
  };

  const saveSharing = async () => {
    if (entries.length === 0) {
      // console.log("No entries to save");
      handleCancel();
      return;
    }
    try {
      // Use selectedFolder if available, otherwise use selectedRow
      const folderData = selectedFolder || selectedRow;
      if (!folderData || !folderData.name) {
        console.error("No folder data available for sharing");
        return;
      }
      const fullPath = currentPath.length > 0
        ? "/" + currentPath.join("/") + "/" + folderData.name
        : "/" + folderData.name;
      const share = new Share(fullPath, folderData.owner);

      // Get Operation IDs by name
      const getOpIds = (opNames) => {
        return opNames.map((name) => {
          const op = { "Read": 1, "Write": 2, "Copy": 3 }[name];
          return op;
        }).filter(id => id !== undefined);
      };

      // console.log("Saving entries:", entries);

      // Add all entries
      for (const entry of entries) {
        const opIdNumbers = getOpIds(entry.opIds);
        const aclEntry = new ActionEntry(
          entry.subject,
          entry.subjectType,
          entry.action,
          opIdNumbers
        );
        await share.addEntry(aclEntry);
      }

      // Share or update share
      if (hadInitialEntries) {
        // console.log("Updating existing share");
        await share.updateShare();
      } else {
        // console.log("Creating new share");
        await share.share();
      }

      handleCancel();
    } catch (e) {
      console.error("Failed to save entries:", e);
    }
  };

  const unShare = async () => {
    try {
      // Use selectedFolder if available, otherwise use selectedRow
      const folderData = selectedFolder || selectedRow;
      if (!folderData || !folderData.name) {
        console.error("No folder data available for unsharing");
        return;
      }
      const fullPath = currentPath.length > 0
        ? "/" + currentPath.join("/") + "/" + folderData.name
        : "/" + folderData.name;
      const share = new Share(fullPath, folderData.owner);
      await share.unShare();
      handleCancel();
    } catch (e) {
      console.error("Failed to unshare:", e);
    }
  };

  // Separate entries by action type
  const allowedEntries = entries.filter(entry => entry.action === "ALLOW");
  const deniedEntries = entries.filter(entry => entry.action === "DENY");

  // Fix 2: Update operations removal handling
  // const handleOperationRemove = (actualIndex, opToRemove) => {
  //   setEntries(prevEntries => {
  //     const newEntries = [...prevEntries];
  //     const entry = {...newEntries[actualIndex]};

  //     // Remove the operation from opIds
  //     entry.opIds = entry.opIds.filter(op => op !== opToRemove);

  //     // If no operations left, add back Read as default
  //     if (entry.opIds.length === 0) {
  //       entry.opIds = ["Read"];
  //     }

  //     newEntries[actualIndex] = entry;
  //     return newEntries;
  //   });
  // };

  // Render a single entry row

  const toggleEditMode = (index, isEditing) => {
    setEntries((prevEntries) => {
      const newEntries = [...prevEntries];
      newEntries[index] = {
        ...newEntries[index],
        isEditing
      };
      return newEntries;
    });
  };
  const renderEntryRow = (entry, idx, entriesList) => {
    const actualIndex = entries.findIndex(e => e === entry);
    const subjectEmailLower = normalizeEmail(entry.subject);
    const avatarImage =
      entry.subjectType === "USER"
        ? (userOptions.find((o) => o.valueLower === subjectEmailLower)?.image ?? null)
        : null;


    return (
      <div className={`share-entry-row ${entry.isEditing ? "editing" : ""}`} key={idx}>
        <div className="user-info">
          <div className="avatar share-avatar-thumbnail">
            <Avatar initial={entry.subject.charAt(0).toUpperCase()} image={avatarImage} size={SHARE_AVATAR_SIZE} />
          </div>
          <div className="user-details">
            <input
              type="text"
              className="subject-input"
              disabled={true}
              value={entry.subject}
              onChange={(e) => handleManualEntry(actualIndex, e.target.value)}
              placeholder={entry.subjectType === "USER" ? "Enter email" : "Enter team name"}
            />
          </div>
        </div>

        <div className="type-selector" data-label="Type">
          <span>{entry.subjectType === "USER" ? "User" : "Team"}</span>
        </div>

        <div className="action-selector" data-label="Action">
          <select
            value={entry.action}
            onChange={(e) => updateEntry(actualIndex, "action", e.target.value)}
            className={`action-select ${entry.action === "DENY" ? "deny" : ""}`}
          >
            <option value="ALLOW">Allow</option>
            <option value="DENY">Deny</option>
          </select>
        </div>

        <div className="operations-selector">
          <div
            className={`custom-multi-select ${entry.menuOpen ? "open" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setEntries(prev => {
                const newEntries = [...prev];
                newEntries[actualIndex] = {
                  ...newEntries[actualIndex],
                  menuOpen: !newEntries[actualIndex].menuOpen
                };
                return newEntries;
              });
            }}
          >
            <div className="multi-select-value">
              {entry.opIds.length > 0 ? (
                <div className="selected-values">
                  {entry.opIds.map((opId) => (
                    <span key={opId} className="selected-tag">
                      {opId}
                      <span
                        className="remove-tag"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          let newOps = entry.opIds.filter(op => op !== opId);
                          if (newOps.length === 0) {
                            newOps = ["Read"];
                          }
                          setEntries(prevEntries => {
                            const newEntries = [...prevEntries];
                            if (newEntries[actualIndex]) {
                              newEntries[actualIndex] = {
                                ...newEntries[actualIndex],
                                opIds: newOps
                              };
                            }
                            return newEntries;
                          });
                        }}
                      >
                        ×
                      </span>
                    </span>
                  ))}
                </div>
              ) : (
                <span className="placeholder">Operations</span>
              )}
              <span className="dropdown-arrow">▼</span>
            </div>
            {entry.menuOpen && (
              <div className="multi-select-menu" onClick={(e) => e.stopPropagation()}>
                {availableOps.map((op) => {
                  const isSelected = entry.opIds.includes(op.value);
                  return (
                    <div
                      key={op.value}
                      className={`multi-select-option ${isSelected ? "selected" : ""}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        let newOps;
                        if (isSelected) {
                          newOps = entry.opIds.filter(opId => opId !== op.value);
                          if (newOps.length === 0) {
                            newOps = ["Read"];
                          }
                        } else {
                          newOps = [...entry.opIds, op.value];
                        }
                        setEntries(prevEntries => {
                          const newEntries = [...prevEntries];
                          if (newEntries[actualIndex]) {
                            newEntries[actualIndex] = {
                              ...newEntries[actualIndex],
                              opIds: newOps
                            };
                          }
                          return newEntries;
                        });
                      }}
                    >
                      <span className="option-checkbox">{isSelected ? "✓" : ""}</span>
                      <span className="option-label">{op.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="remove-button">
          {entry.isNew ? (
            // For new entries, show just the X button
            <button
              onClick={(e) => {
                e.stopPropagation();
                removeEntry(actualIndex);
              }}
              className="icon-button"
            // disabled={entry.subjectType === "TEAM"}
            >
              ✕
            </button>
          ) : (
            // For existing entries, show delete button only
            <div className="entry-actions">
              {/* Delete button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm("Remove this shared user?")) {
                    removeEntry(actualIndex);
                  }
                }}
                className="icon-button delete-button"
                title="Delete"
              // disabled={entry.subjectType === "TEAM"}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M3 6H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M8 6V4C8 3.44772 8.44772 3 9 3H15C15.5523 3 16 3.44772 16 4V6" stroke="currentColor" strokeWidth="2" />
                  <path d="M19 6V20C19 20.5523 18.5523 21 18 21H6C5.44772 21 5 20.5523 5 20V6" stroke="currentColor" strokeWidth="2" />
                  <path d="M10 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <WideModal
      heading={`Share "${title}"`}
      handleCancel={handleCancel}
      showFooter={false}
      onClick={(e) => {
        // Stop propagation for dropdown menus
        if (e.target.closest(".multi-select-menu") ||
          e.target.closest(".operations-selector") ||
          e.target.closest(".remove-tag")) {
          e.stopPropagation();
          e.preventDefault();
        }
      }}
    >
      <div className="share-project">
        <div className="share-container" >
          {/* Search input with autocomplete */}
          <div className="share-with-section" ref={autocompleteRef}>
            <div className="autocomplete-container">
              <div className="search-row">
                <svg
                  className="person-icon"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"
                    fill="currentColor"
                  />
                </svg>
                <input
                  type="text"
                  value={inputValue}
                  onChange={handleInputChange}
                  onClick={() => {
                    setShowOptions(true);
                  }}
                  placeholder={inputMode === "user" ? "Search by email" : "Enter team name"}
                  className="share-input"
                // disabled={inputMode === "team"}
                />

                <div className="toggle-container">
                  {/* User/Team toggle */}
                  <div className="input-mode-toggle">
                    <button
                      className={`toggle-button ${inputMode === "user" ? "active" : ""}`}
                      onClick={() => setInputMode("user")}
                    >
                      User
                    </button>
                    <button
                      className={`toggle-button ${inputMode === "team" ? "active" : ""}`}
                      onClick={() => setInputMode("team")}
                    >
                      Team
                    </button>
                  </div>

                  {/* Allow/Deny toggle */}
                  <div className="input-action-toggle">
                    <button
                      className={`toggle-button ${inputAction === "ALLOW" ? "active" : ""}`}
                      onClick={() => setInputAction("ALLOW")}
                    >
                      Allow
                    </button>
                    <button
                      className={`toggle-button ${inputAction === "DENY" ? "active deny" : ""}`}
                      onClick={() => setInputAction("DENY")}
                    >
                      Deny
                    </button>
                  </div>
                </div>
              </div>

              {showOptions && filteredOptions.length > 0 && (
                <div className="options-dropdown">
                  {filteredOptions.map((option) => (
                    <div
                      key={option.value}
                      className="option-item"
                      onClick={() => {
                        if (inputMode === "user") {
                          addNewSharedUser(option);
                        } else {
                          // Add team
                          const newEntry = defaultEntry();
                          newEntry.subject = option.value;
                          newEntry.subjectType = "TEAM";
                          newEntry.action = inputAction;
                          newEntry.isNew = false;
                          setEntries((prev) => [...prev, newEntry]);
                          setInputValue("");
                          setShowOptions(false);
                        }
                      }}
                    >
                      <div className="avatar share-avatar-thumbnail">
                        <Avatar initial={option.initial} image={option.image} size={SHARE_AVATAR_SIZE} />
                      </div>
                      <div className="email">{option.label}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* {inputMode === "team" && inputValue && filteredOptions.length === 0 && (
                <div className="add-team-button">
                  <button
                    onClick={() => {
                      const newEntry = defaultEntry();
                      newEntry.subject = inputValue;
                      newEntry.subjectType = "TEAM";
                      newEntry.action = inputAction;
                      setEntries((prev) => [...prev, newEntry]);
                      setInputValue("");
                    }}
                  >
                    Add Team: {inputValue}
                  </button>
                </div>
              )} */}
            </div>
          </div>

          {/* People with allowed access section */}
          <div className="people-with-access">
            <h3>People with allowed access</h3>

            {isLoading ? (
              <div className="loading">Loading shared users...</div>
            ) : allowedEntries.length === 0 && deniedEntries.length === 0 ? (
              <div className="no-shares">No one has access yet</div>
            ) : allowedEntries.length === 0 ? (
              <div className="no-shares">No one is allowed access</div>
            ) : (
              <div className="share-entries">
                {allowedEntries.map((entry, idx) => renderEntryRow(entry, idx, allowedEntries))}
              </div>
            )}
          </div>

          {/* People with denied access section - only shown if there are denied entries */}
          {deniedEntries.length > 0 && (
            <div className="people-with-access denied-access">
              <h3>People with denied access</h3>
              <div className="share-entries">
                {deniedEntries.map((entry, idx) => renderEntryRow(entry, idx, deniedEntries))}
              </div>
            </div>
          )}

          {/* Add new entry button */}
          {/* <div className="add-entry-section">
            <button className="add-entry-button" onClick={addNewEntry}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor" />
              </svg>
              Add another person or team
            </button>
          </div> */}

          {/* Footer buttons */}
          {/* // Add this style to ensure footer buttons stay visible */}
          <div className="share-footer" >
            <Button
              title="Cancel"
              type="violet"
              onClick={handleCancel}
              width="100px"
            />
            <div className="right-buttons">
              {entries.length > 0 && (
                <Button
                  title="Unshare All"
                  type="violet"
                  onClick={unShare}
                  width="120px"
                />
              )}
              <Button
                title="Save"
                type="violet"
                onClick={saveSharing}
                width="100px"
                disabled={entries.length === 0}
              />
            </div>
          </div>
        </div>
      </div>
    </WideModal>
  );
};

export default ShareProject;